import React, { useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw, Mail, Shield, Users as UsersIcon, Eye, CreditCard as Edit, Trash2, UserPlus, Download, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout/Layout';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';
import { createUserWithRole } from '../utils/callEdge';
import type { Database } from '../types/supabase';

type Db = Database['public'];
type UserRow = Db['Tables']['users']['Row'];

interface ProjectOwner extends UserRow {
  projects_count?: number;
  active_projects_count?: number;
  completed_projects_count?: number;
  last_activity?: string;
  total_funding_requested?: number;
  total_funding_received?: number;
}

export const ProjectOwnersAdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { user: me } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [owners, setOwners] = useState<ProjectOwner[]>([]);
  const [search, setSearch] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [organizationFilter, setOrganizationFilter] = useState('');
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const canAdmin = me && ['COORDINATEUR', 'ADMIN'].includes(me.profile_type || '');

  const loadOwners = async () => {
    setLoading(true);
    try {
      // 1) Récupérer tous les porteurs
      const { data: users, error } = await supabase
        .from('users')
        .select('*')
        .eq('profile_type', 'PORTEUR')
        .order('created_at', { ascending: false });

      if (error) throw error;

      let list: ProjectOwner[] = users ?? [];

      // 2) Enrichir avec les statistiques de projets
      if (list.length > 0) {
        const userIds = list.map(u => u.id);
        
        // Compter les projets par statut
        const { data: projects, error: projErr } = await supabase
          .from('projects')
          .select('owner_id, status, funding_requested, funding_amount, updated_at')
          .in('owner_id', userIds);

        if (projErr) throw projErr;

        // Calculer les statistiques par utilisateur
        const stats = new Map<string, {
          total: number;
          active: number;
          completed: number;
          lastActivity: string;
          fundingRequested: number;
          fundingReceived: number;
        }>();

        (projects ?? []).forEach(p => {
          const ownerId = p.owner_id as string;
          const current = stats.get(ownerId) || {
            total: 0,
            active: 0,
            completed: 0,
            lastActivity: '',
            fundingRequested: 0,
            fundingReceived: 0
          };

          current.total++;
          
          if (['EN_COURS', 'VALIDE', 'DEBLOCAGE_FONDS'].includes(p.status)) {
            current.active++;
          }
          
          if (p.status === 'TERMINE') {
            current.completed++;
          }

          if (!current.lastActivity || p.updated_at > current.lastActivity) {
            current.lastActivity = p.updated_at;
          }

          current.fundingRequested += p.funding_requested || 0;
          current.fundingReceived += p.funding_amount || 0;

          stats.set(ownerId, current);
        });

        // Enrichir la liste avec les statistiques
        list = list.map(u => {
          const userStats = stats.get(u.id) || {
            total: 0, active: 0, completed: 0, lastActivity: '', 
            fundingRequested: 0, fundingReceived: 0
          };
          
          return {
            ...u,
            projects_count: userStats.total,
            active_projects_count: userStats.active,
            completed_projects_count: userStats.completed,
            last_activity: userStats.lastActivity || u.updated_at,
            total_funding_requested: userStats.fundingRequested,
            total_funding_received: userStats.fundingReceived
          };
        });
      }

      setOwners(list);
    } catch (e: any) {
      console.error('loadOwners', e);
      alert(`Erreur chargement porteurs: ${e.message ?? e}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    if (canAdmin) {
      loadOwners(); 
    }
  }, [canAdmin]);

  const filtered = useMemo(() => {
    let result = owners;

    // Filtre par recherche
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(u =>
        (u.full_name ?? '').toLowerCase().includes(q) ||
        (u.email ?? '').toLowerCase().includes(q) ||
        (u.organization ?? '').toLowerCase().includes(q)
      );
    }

    // Filtre par ville
    if (cityFilter) {
      result = result.filter(u => u.city === cityFilter);
    }

    // Filtre par organisation
    if (organizationFilter) {
      result = result.filter(u => u.organization === organizationFilter);
    }

    return result;
  }, [owners, search, cityFilter, organizationFilter]);

  const resetPassword = async (userId: string, email?: string | null) => {
    if (!canAdmin) return;
    if (!confirm(`Réinitialiser le mot de passe pour ${email ?? userId} ?`)) return;

    try {
      setSendingId(userId);
      
      // Utiliser l'API Supabase Auth pour réinitialiser le mot de passe
      const { error } = await supabase.auth.admin.generateLink({
        type: 'recovery',
        email: email!,
        options: {
          redirectTo: `${window.location.origin}/auth/callback?type=recovery`
        }
      });

      if (error) throw error;

      alert(`Email de réinitialisation envoyé à ${email}`);
    } catch (e: any) {
      console.error('resetPassword', e);
      alert(`Erreur: ${e.message ?? e}`);
    } finally {
      setSendingId(null);
    }
  };

  const createPorteur = async (formData: {
    email: string;
    full_name: string;
    phone?: string;
    organization?: string;
    city?: string;
  }) => {
    if (!canAdmin) return;

    setIsCreating(true);
    try {
      const { userId, error } = await createUserWithRole(
        formData.email,
        'TempPassword123!', // Mot de passe temporaire
        {
          role: 'porteur',
          metadata: {
            full_name: formData.full_name,
            phone: formData.phone,
            organization: formData.organization
          },
          profile_data: {
            profile_type: 'PORTEUR',
            full_name: formData.full_name,
            phone: formData.phone,
            organization: formData.organization,
            city: formData.city
          }
        }
      );

      if (error) throw new Error(error);

      alert(`Porteur créé avec succès ! Email: ${formData.email}, Mot de passe temporaire: TempPassword123!`);
      setShowCreateModal(false);
      loadOwners(); // Recharger la liste
    } catch (e: any) {
      console.error('createPorteur', e);
      alert(`Erreur création: ${e.message ?? e}`);
    } finally {
      setIsCreating(false);
    }
  };

  const exportPorteurs = () => {
    const csvContent = [
      ['Nom', 'Email', 'Organisation', 'Ville', 'Téléphone', 'Projets', 'Projets actifs', 'Financement demandé', 'Financement reçu', 'Créé le'].join(','),
      ...filtered.map(u => [
        u.full_name || '',
        u.email || '',
        u.organization || '',
        u.city || '',
        u.phone || '',
        u.projects_count || 0,
        u.active_projects_count || 0,
        u.total_funding_requested || 0,
        u.total_funding_received || 0,
        new Date(u.created_at!).toLocaleDateString('fr-FR')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `porteurs-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Extraire les valeurs uniques pour les filtres
  const cities = [...new Set(owners.map(u => u.city).filter(Boolean))].sort();
  const organizations = [...new Set(owners.map(u => u.organization).filter(Boolean))].sort();

  if (!canAdmin) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center py-12">
            <Shield className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Accès Restreint</h2>
            <p className="text-gray-600">
              Seuls les coordinateurs et administrateurs peuvent accéder à cette page.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <UsersIcon className="w-8 h-8 text-green-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Porteurs de Projet</h1>
              <p className="text-gray-600">Gestion des utilisateurs porteurs de projet ESS</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={exportPorteurs}
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Exporter CSV</span>
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              <span>Nouveau porteur</span>
            </button>
            <button
              onClick={loadOwners}
              disabled={loading}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Actualiser</span>
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total porteurs</p>
                <p className="text-2xl font-bold text-gray-900">{owners.length}</p>
              </div>
              <UsersIcon className="w-8 h-8 text-green-400" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Projets actifs</p>
                <p className="text-2xl font-bold text-blue-600">
                  {owners.reduce((sum, u) => sum + (u.active_projects_count || 0), 0)}
                </p>
              </div>
              <Eye className="w-8 h-8 text-blue-400" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Projets terminés</p>
                <p className="text-2xl font-bold text-green-600">
                  {owners.reduce((sum, u) => sum + (u.completed_projects_count || 0), 0)}
                </p>
              </div>
              <Shield className="w-8 h-8 text-green-400" />
            </div>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Financement total</p>
                <p className="text-2xl font-bold text-purple-600">
                  {(owners.reduce((sum, u) => sum + (u.total_funding_received || 0), 0) / 1000).toFixed(0)}K€
                </p>
              </div>
              <Mail className="w-8 h-8 text-purple-400" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Rechercher par nom, email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* City Filter */}
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Toutes les villes</option>
              {cities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>

            {/* Organization Filter */}
            <select
              value={organizationFilter}
              onChange={(e) => setOrganizationFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Toutes les organisations</option>
              {organizations.map(org => (
                <option key={org} value={org}>{org}</option>
              ))}
            </select>

            {/* Results count */}
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Filter className="w-4 h-4" />
              <span>{filtered.length} porteur{filtered.length > 1 ? 's' : ''}</span>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Porteur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Projets
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Financement
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dernière activité
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        <span className="ml-2">Chargement...</span>
                      </div>
                    </td>
                  </tr>
                )}
                
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      {search || cityFilter || organizationFilter 
                        ? 'Aucun porteur ne correspond aux filtres sélectionnés'
                        : 'Aucun porteur de projet enregistré'
                      }
                    </td>
                  </tr>
                )}
                
                {!loading && filtered.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-medium text-sm">
                            {u.full_name?.charAt(0) || u.email?.charAt(0) || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{u.full_name || 'Nom non renseigné'}</p>
                          <p className="text-sm text-gray-600">{u.organization || 'Organisation non renseignée'}</p>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm text-gray-900">{u.email}</p>
                        {u.phone && (
                          <p className="text-sm text-gray-600">{u.phone}</p>
                        )}
                        {u.city && (
                          <p className="text-sm text-gray-600">{u.city}</p>
                        )}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {u.projects_count || 0} total
                        </p>
                        <div className="flex items-center space-x-2 text-xs">
                          <span className="text-blue-600">{u.active_projects_count || 0} actifs</span>
                          <span className="text-gray-300">•</span>
                          <span className="text-green-600">{u.completed_projects_count || 0} terminés</span>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {(u.total_funding_received || 0).toLocaleString('fr-FR')} €
                        </p>
                        <p className="text-xs text-gray-600">
                          sur {(u.total_funding_requested || 0).toLocaleString('fr-FR')} € demandés
                        </p>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-900">
                        {u.last_activity 
                          ? new Date(u.last_activity).toLocaleDateString('fr-FR')
                          : 'Jamais'
                        }
                      </p>
                    </td>
                    
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => navigate(`/admin/users/${u.id}`)}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                          title="Voir les détails"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => resetPassword(u.id, u.email)}
                          disabled={sendingId === u.id}
                          className="p-2 text-blue-600 hover:text-blue-700 rounded-lg transition-colors disabled:opacity-50"
                          title="Réinitialiser le mot de passe"
                        >
                          {sendingId === u.id ? (
                            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Mail className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <CreatePorteurModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onSubmit={createPorteur}
            isLoading={isCreating}
          />
        )}
      </div>
    </Layout>
  );
};

// Modal de création de porteur
interface CreatePorteurModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<void>;
  isLoading: boolean;
}

const CreatePorteurModal: React.FC<CreatePorteurModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading
}) => {
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    phone: '',
    organization: '',
    city: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.full_name) return;
    
    await onSubmit(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Créer un porteur</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="porteur@example.com"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom complet *
            </label>
            <input
              type="text"
              required
              value={formData.full_name}
              onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Marie Dupont"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Téléphone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="0590 XX XX XX"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Organisation
            </label>
            <input
              type="text"
              value={formData.organization}
              onChange={(e) => setFormData(prev => ({ ...prev, organization: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Coopérative Agricole"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ville
            </label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Basse-Terre"
            />
          </div>

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Mot de passe temporaire :</strong> TempPassword123!
              <br />
              <span className="text-xs">Le porteur devra le changer à sa première connexion</span>
            </p>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading || !formData.email || !formData.full_name}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Création...</span>
                </div>
              ) : (
                'Créer le porteur'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};